// 保存単語の一覧。クラウド同期された vocab を表示し、検索・意味の展開・発音・削除ができる。
// SRS の状態（新規/復習可/次回期限）も各語に出す。採点は「復習」タブで。
import { listVocabStatus, removeWord } from '../lib/vocab.js'
import { speak, ttsAvailable } from '../lib/tts.js'
import { bgSync } from '../lib/sync.js'
import { isConnected as gdriveConnected } from '../lib/gdrive.js'
import { icon } from '../lib/icons.js'
import { t } from '../lib/i18n.js'

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
}

// 未来時刻(ISO) → 「10分」「3日」等のざっくり相対表記。
function fmtUntil(iso) {
  const ms = new Date(iso).getTime() - Date.now()
  if (!Number.isFinite(ms)) return ''
  const m = Math.round(ms / 60000)
  if (m < 60) return t('time.min', { n: Math.max(1, m) })
  const h = Math.round(m / 60)
  if (h < 24) return t('time.hour', { n: h })
  const d = Math.round(h / 24)
  if (d < 30) return t('time.day', { n: d })
  return t('time.month', { n: Math.round(d / 30) })
}

// 訳文の配列 → HTML（先頭の {品詞} を強調）。
function senseHtml(senses) {
  return (senses || [])
    .slice(0, 8)
    .map((e) => {
      const m = e.match(/^(\{[^}]*\})\s*(.*)$/)
      return m
        ? `<div class="sense"><span class="pos">${escapeHtml(m[1])}</span>${escapeHtml(m[2])}</div>`
        : `<div class="sense">${escapeHtml(e)}</div>`
    })
    .join('')
}

// SRS 状態のチップ。
function statusChip(w) {
  if (w.isNew) return `<span class="badge">${t('words.statusNew')}</span>`
  if (w.overdue) return `<span class="badge due">${t('words.statusDue')}</span>`
  return `<span class="badge muted-badge">${t('words.next', { t: fmtUntil(w.dueIso) })}</span>`
}

function rowEl(w) {
  const el = document.createElement('div')
  el.className = 'item word-row'
  el.dataset.key = w.key
  const firstSense = (w.senses && w.senses[0]) ? w.senses[0].replace(/^\{[^}]*\}\s*/, '') : ''
  el.innerHTML = `
    <div class="item-body">
      <div class="word-head">
        <span class="word-term">${escapeHtml(w.word)}</span>
        ${statusChip(w)}
      </div>
      <div class="item-meta muted word-1stsense">${escapeHtml(firstSense)}</div>
      <div class="word-detail" hidden>
        <div class="rv-back">${senseHtml(w.senses)}</div>
        ${w.context ? `<div class="word-ctx muted">${escapeHtml(w.context)}</div>` : ''}
        ${w.source && w.source.itemName ? `<div class="word-src muted"><span class="src-pin">${icon('pin')}</span>${escapeHtml(w.source.itemName)}</div>` : ''}
      </div>
    </div>
    <div class="item-action">
      ${ttsAvailable() ? `<button class="mini secondary" data-act="tts" aria-label="${t('common.speak')}">${icon('speaker')}</button>` : ''}
      <button class="mini danger" data-act="remove">${t('common.remove')}</button>
    </div>`
  return el
}

export async function renderWords(root) {
  root.innerHTML = `
    <div class="head-row">
      <h1>${t('words.title')}</h1>
      <div style="display:flex;gap:8px;align-items:center">
        <span class="muted" id="w-count" style="font-size:13px"></span>
        <button class="mini" id="w-refresh">${t('sync.refresh')}</button>
      </div>
    </div>
    <div class="muted" id="w-sync" style="font-size:12px;min-height:16px;margin-bottom:6px"></div>
    <input id="w-search" class="w-search" type="search" placeholder="${t('words.searchPlaceholder')}" autocomplete="off">
    <div id="w-list"></div>`

  const listEl = root.querySelector('#w-list')
  const countEl = root.querySelector('#w-count')
  const searchEl = root.querySelector('#w-search')
  const refreshEl = root.querySelector('#w-refresh')
  const syncEl = root.querySelector('#w-sync')

  let words = await listVocabStatus()
  countEl.textContent = t('words.count', { n: words.length })

  // 単語リストを取り直して再描画（検索文字列は維持）。
  async function reload() {
    words = await listVocabStatus()
    countEl.textContent = t('words.count', { n: words.length })
    draw(searchEl.value)
  }

  function draw(q) {
    const query = (q || '').trim().toLowerCase()
    const shown = query
      ? words.filter(
          (w) =>
            w.word.toLowerCase().includes(query) ||
            (w.senses || []).some((s) => s.toLowerCase().includes(query))
        )
      : words
    listEl.innerHTML = ''
    if (!shown.length) {
      listEl.innerHTML = `<p class="muted">${
        words.length ? t('words.noMatch') : t('words.empty')
      }</p>`
      return
    }
    for (const w of shown) listEl.appendChild(rowEl(w))
  }

  // 行タップで意味を展開／折りたたみ。ボタン（発音/削除）は別処理。
  listEl.onclick = async (ev) => {
    const btn = ev.target.closest('button[data-act]')
    const row = ev.target.closest('.word-row')
    if (!row) return
    const key = row.dataset.key
    const w = words.find((x) => x.key === key)
    if (btn) {
      if (btn.dataset.act === 'tts') {
        if (w) speak(w.word)
      } else if (btn.dataset.act === 'remove') {
        await removeWord(key)
        words = words.filter((x) => x.key !== key)
        countEl.textContent = t('words.count', { n: words.length })
        row.remove()
        bgSync() // 削除（tombstone）を裏でクラウドへ反映
      }
      return
    }
    const detail = row.querySelector('.word-detail')
    if (detail) detail.hidden = !detail.hidden
  }

  searchEl.oninput = () => draw(searchEl.value)
  draw('')

  // 手動更新：押した瞬間ボタンを「同期中…」に → 完了後に件数を表示しボタンを戻す。
  let busy = false
  refreshEl.onclick = async () => {
    if (busy) return
    busy = true
    refreshEl.disabled = true
    refreshEl.textContent = t('sync.syncing')
    syncEl.textContent = ''
    try {
      const r = await bgSync()
      if (r && r.skipped) {
        syncEl.textContent = '' // 未連携：静かに何もしない
      } else if (r && r.ok) {
        await reload()
        const n = r.vocab ? r.vocab.added : 0
        syncEl.textContent = n > 0 ? t('sync.got', { n }) : t('sync.upToDate')
      } else {
        syncEl.textContent = ''
      }
    } finally {
      busy = false
      refreshEl.disabled = false
      refreshEl.textContent = t('sync.refresh')
    }
  }

  // 開いた時に裏で自動 pull（連携時のみ）。変化があれば静かに再描画。
  ;(async () => {
    if (!gdriveConnected()) return
    const r = await bgSync()
    if (r && r.ok && r.vocab && (r.vocab.added > 0 || r.vocab.merged > 0)) await reload()
  })()
}
